/**
 * Risk Flags Card
 *
 * Displays warning flags about the vehicle that may affect the deal.
 * Only shown when there are active risk flags.
 */

import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import type { RiskFlags } from '../lib/types';
import { cn } from '@/lib/utils';

interface RiskFlagsCardProps {
  riskFlags: RiskFlags;
  className?: string;
}

/**
 * Check if there are any active risk flags worth showing
 */
export function hasActiveRiskFlags(flags: RiskFlags): boolean {
  // Don't show electric as a "risk" - it's informational
  return flags.isImport || flags.missingCo2 || flags.missingBpm || flags.missingFirstRegDate;
}

export function RiskFlagsCard({ riskFlags, className }: RiskFlagsCardProps) {
  const { t } = useTranslation('common');

  // Build list of active flags
  const flags: { key: string; label: string }[] = [];

  if (riskFlags.isImport) {
    flags.push({
      key: 'import',
      label: t('calculator.riskImport', 'Geïmporteerd voertuig'),
    });
  }

  if (riskFlags.missingCo2) {
    flags.push({
      key: 'co2',
      label: t('calculator.riskMissingCo2', 'CO₂-uitstoot ontbreekt in RDW'),
    });
  }

  if (riskFlags.missingBpm) {
    flags.push({
      key: 'bpm',
      label: t('calculator.riskMissingBpm', 'Bruto BPM ontbreekt in RDW'),
    });
  }

  if (riskFlags.missingFirstRegDate) {
    flags.push({
      key: 'firstReg',
      label: t('calculator.riskMissingFirstReg', 'Eerste registratiedatum ontbreekt'),
    });
  }

  // Don't render if no flags
  if (flags.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20 p-4',
        className
      )}
    >
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <h3 className="font-semibold text-amber-800 dark:text-amber-300">
          {t('calculator.riskFlagsTitle', 'Let op')}
        </h3>
      </div>

      <ul className="space-y-1">
        {flags.map((flag) => (
          <li
            key={flag.key}
            className="text-sm text-amber-700 dark:text-amber-400 flex items-start gap-2"
          >
            <span className="text-amber-500">•</span>
            <span>{flag.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
